import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import { getFormValidationTranslations } from '@common/i18n/formValidation';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { FormHelperText } from '@mui/material';
import Autocomplete, { type AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type { SyntheticEvent } from 'react';
import { useRef, useState } from 'react';
import { formatPlaceAddress, getPlaceDetails } from '@/utils/addressLookup';

const AutocompleteService = window.google
    ? new window.google.maps.places.AutocompleteService()
    : null;

interface AddressOption {
    value: string;
    label: string;
    placeId: string;
}

interface Props {
    tabindex?: string | number;
    name: string;
    value: string;
    onChange(value: string): void;
    isError?: boolean;
    disabled?: boolean;
    helperText?: string;
    placeholder?: string;
    language?: TemplateFormLanguage;
}

export default function GooglePlacesAutocomplete({
    tabindex,
    name,
    value,
    isError,
    onChange,
    disabled = false,
    helperText,
    placeholder = '',
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
}: Props) {
    const [options, setOptions] = useState<AddressOption[]>([]);
    const [localError, setLocalError] = useState<string | null>(null);
    // Shared by the suggestion requests and the details request of the address being typed, so
    // Google bills them as one lookup instead of one per keystroke.
    const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
    const latestInputRef = useRef('');
    const t = getFormValidationTranslations(language);

    /** Fetch suggestions from Google Places API */
    const fetchPlaceSuggestions = (input: string) => {
        latestInputRef.current = input;
        if (!AutocompleteService || !input) {
            setOptions([]);
            return;
        }

        sessionTokenRef.current ??= new google.maps.places.AutocompleteSessionToken();
        const request = {
            input,
            sessionToken: sessionTokenRef.current,
            // Only suggestions are limited. Other addresses can still be typed in full.
            componentRestrictions: { country: ['ca', 'us'] },
        };
        AutocompleteService.getPlacePredictions(request, (results, status) => {
            // Responses can arrive out of order. Only show the ones for what's typed now.
            if (input !== latestInputRef.current) {
                return;
            }
            if (status === 'OK' && results) {
                setOptions(
                    results.map((result) => ({
                        value: result.description,
                        label: result.description,
                        placeId: result.place_id,
                    })),
                );
            } else {
                setOptions([]);
            }
        });
    };

    const handleInputChange = (
        _event: SyntheticEvent,
        newInputValue: string,
        reason: AutocompleteInputChangeReason,
    ) => {
        onChange(newInputValue); // Update the parent state
        // `reset` sets the input to a picked suggestion, which needs no new suggestions.
        if (reason !== 'reset') {
            fetchPlaceSuggestions(newInputValue);
        }

        // Show error if input is cleared
        if (!newInputValue) {
            setLocalError(t.selectAddressOrEnterManually);
        } else {
            setLocalError(null);
        }
    };

    /** A picked suggestion, or the typed text when Enter is pressed without picking one. */
    const handleOptionChange = (_event: SyntheticEvent, newValue: AddressOption | string) => {
        setLocalError(null);
        if (typeof newValue === 'string') {
            onChange(newValue);
            return;
        }

        const selectedDescription = newValue.value;
        if (!newValue.placeId || !window.google) {
            onChange(selectedDescription);
            return;
        }

        const sessionToken = sessionTokenRef.current ?? undefined;
        sessionTokenRef.current = null;
        void getPlaceDetails(newValue.placeId, sessionToken).then((place) => {
            onChange((place && formatPlaceAddress(place)) || selectedDescription);
        });
    };

    return (
        <div>
            <Autocomplete
                freeSolo
                disableClearable
                options={options}
                inputValue={value || ''} // Controlled input value
                onChange={handleOptionChange}
                onInputChange={handleInputChange}
                disabled={disabled}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        name={name}
                        inputProps={{ ...params.inputProps, tabindex: tabindex }}
                        sx={{
                            marginTop: '4px',
                            width: '100%',
                            fontSize: '14px',
                            '& .MuiOutlinedInput-input': {
                                padding: '8px 12px',
                            },
                            '& .MuiFormHelperText-root': {
                                marginLeft: '0px',
                            },
                        }}
                        error={!!localError || isError}
                        placeholder={placeholder}
                    />
                )}
            />
            <FormHelperText error={!!localError || isError}>
                {localError || (isError ? helperText || t.validAddress : '')}
            </FormHelperText>
        </div>
    );
}
